import { IOptionItem } from "@/components/ui/dropdown/types";
import { DROPDOWN_ALL_VALUE } from "@/components/ui/dropdownMultiSelect";
import { DatasetData, DatasetDataType } from "@/types/dataset";
import { EDataSettingSpatialType } from "@/types/dataSetting";

import {
  AGGREGATION_CALCULATION_OPTIONS,
  CATEGORY_OPTIONS,
  METHOD_OPTIONS,
  SORT_BY_OPTIONS,
  SORT_BY_PIVOT_OPTIONS,
  SORT_OPTIONS
} from "./constants";
import { DatasetSectionViewType } from "./datasetSection";
import {
  AnalysisConfiguration,
  ECategoryTransform,
  TransformationMethod
} from "./types";

import { GridOptionItem } from ".";

export interface DatasetSchema {
  schema: Array<{
    id: string;
    code?: string;
    attribute_name: string;
    data_type: string;
    unique_values_count?: number;
  }>;
}

export function getDefaultAnalysisConfig(
  dataset: DatasetData,
  hasNumericColumn: boolean,
  attributesOptions: IOptionItem[]
): AnalysisConfiguration {
  if (
    !hasNumericColumn ||
    dataset.spatial_aggregation_type === EDataSettingSpatialType.Point ||
    dataset.spatial_aggregation_type === EDataSettingSpatialType.Line ||
    dataset?.timeseries
  ) {
    return {
      method: METHOD_OPTIONS[0], // Pivot
      sort: SORT_OPTIONS[1], // Desc
      filterBy: [DROPDOWN_ALL_VALUE],
      calculationMethod: AGGREGATION_CALCULATION_OPTIONS[1], // COUNT
      category: CATEGORY_OPTIONS[1], // Display Row
      sortBy: SORT_BY_PIVOT_OPTIONS[0], // Agg (Count)
      attribute_x: attributesOptions?.[0], // First attribute
      attribute_y: attributesOptions?.[0] // First attribute
    };
  }
  if (dataset.rows.length === 0) return {};

  if (dataset.rows.length === 1) {
    return {
      method: METHOD_OPTIONS[1], // Transpose
      sort: SORT_OPTIONS[1], // Desc
      filterBy: [DROPDOWN_ALL_VALUE], // All
      category: CATEGORY_OPTIONS[0], // Display Row
      calculationMethod: AGGREGATION_CALCULATION_OPTIONS[1], // COUNT
      sortBy: SORT_BY_OPTIONS[0]
    };
  }
  if (dataset.rows.length <= 3) {
    return {
      method: METHOD_OPTIONS[1], // Transpose
      sort: SORT_OPTIONS[1], // Desc
      filterBy: [DROPDOWN_ALL_VALUE],
      category: CATEGORY_OPTIONS[1], // Display Row
      attribute_x: attributesOptions?.[0], // First attribute
      calculationMethod: AGGREGATION_CALCULATION_OPTIONS[1], // COUNT
      sortBy: SORT_BY_OPTIONS[0], // Agg (Count)
      attribute_y: attributesOptions?.[0] // First attribute
    };
  }
  return {
    method: METHOD_OPTIONS[1], // Transpose
    category: CATEGORY_OPTIONS[1], // Display Row
    calculationMethod: AGGREGATION_CALCULATION_OPTIONS[2], // AVG
    sort: SORT_OPTIONS[1], // Desc
    filterBy: [DROPDOWN_ALL_VALUE],
    sortBy: SORT_BY_OPTIONS[0] // Agg (Count)
  };
}

export const getDatasetSummarySortByOptions = (
  analysisConfiguration: AnalysisConfiguration | null,
  gridsSelected: string[],
  gridValue: GridOptionItem[]
) => {
  const isPivot =
    analysisConfiguration?.method?.value === TransformationMethod.PIVOT;
  const isDisplayRowsAgg =
    analysisConfiguration?.category?.value ===
    ECategoryTransform.DISPLAY_ROWS_AGG;
  const isDisplayRowsSeries =
    analysisConfiguration?.category?.value ===
    ECategoryTransform.DISPLAY_ROWS_SERIES;
  const isGridSelected = gridsSelected.length > 0 && gridsSelected[0] !== "all";

  if (isPivot) return SORT_BY_PIVOT_OPTIONS;

  if (isDisplayRowsSeries && gridsSelected?.[0] === DROPDOWN_ALL_VALUE)
    return gridValue.slice(0, 3).map((item) => ({
      label: item.value,
      value: item.value
    }));

  if (isDisplayRowsAgg && isGridSelected) {
    const filteredOptions = SORT_BY_OPTIONS.filter((item) =>
      item.value
        .toUpperCase()
        .includes(analysisConfiguration?.calculationMethod?.value ?? "")
    );

    return filteredOptions.length > 0 ? filteredOptions : SORT_BY_OPTIONS;
  }

  if (gridsSelected?.[0] === DROPDOWN_ALL_VALUE) {
    if (analysisConfiguration?.calculationMethod?.value) {
      const filteredOptions = SORT_BY_OPTIONS.filter((item) =>
        item.value
          .toUpperCase()
          .includes(analysisConfiguration.calculationMethod?.value ?? "")
      );

      if (filteredOptions.length > 0) return filteredOptions;
    }

    return SORT_BY_OPTIONS;
  }

  return gridsSelected.map(
    (grid) =>
      ({
        label: grid,
        value: grid
      }) as IOptionItem
  );
};

export const getFilteredGrids = (
  categoryValue: string | undefined,
  gridsSelected: string[],
  gridValue: Array<{ value: string }>
): string[] => {
  if (
    categoryValue === ECategoryTransform.DISPLAY_ROWS_SERIES &&
    gridsSelected[0] === DROPDOWN_ALL_VALUE
  ) {
    return gridValue.slice(0, 3).map((item) => item.value) ?? [];
  }
  return gridsSelected;
};

/**
 * Determines the default view type based on dataset properties
 * If it's a POI dataset with 3+ unique values in the first attribute, use DONUT_CHART
 * Otherwise, use TABLE
 */
export function getDefaultViewType(
  dataset: DatasetData,
  attributesOptions: IOptionItem[],
  datasetSchema: DatasetSchema | undefined
): DatasetSectionViewType {
  if (attributesOptions?.length && datasetSchema && dataset) {
    const firstAttributeOption = attributesOptions[0];
    const matchingSchemaItem = datasetSchema.schema.find(
      (item) => item.id === firstAttributeOption.value
    );

    if (
      dataset.spatial_aggregation_type === "poi" &&
      (matchingSchemaItem?.unique_values_count || 0) >= 3
    ) {
      return DatasetSectionViewType.DONUT_CHART;
    }

    if (
      (dataset.spatial_aggregation_type === "polygon" &&
        dataset.columns.some(
          (data) =>
            data.data_type === DatasetDataType.TYPE_DATETIME ||
            data.data_type === DatasetDataType.TYPE_DATE ||
            data.data_type === DatasetDataType.TYPE_TIME
        )) ||
      dataset.timeseries
    ) {
      return DatasetSectionViewType.VERTICAL_BAR;
    }
  }

  return DatasetSectionViewType.TABLE;
}
